/**
 * PlaytestRigApplier (2026-05-13) — auto-applies the playtest rig from
 * `DevConfig.playtestRig` on mount.
 *
 * Goal: I (Claude / agent) edit one field in devConfig.ts; DJ hard-reloads;
 * the debug viewport is exactly what we want with zero manual clicks.
 *
 * The applier runs once per mount of the game (inside WindowManagerProvider).
 * It forces the listed tools open + flips the trackers ON. Idempotent —
 * if DJ closes a window during play, reloading reapplies the rig.
 *
 * Production builds skip the applier (gated on isDevMode).
 *
 * Mounted in src/main.tsx alongside WindowLauncher / WindowHost.
 */

import React, { useEffect } from 'react';
import { DevConfig } from '@/config/environment/devConfig';
import { useWindows } from '../ui/framework/WindowManagerContext';
import { type WindowId } from '../ui/framework/types';
import { FrameJitterTracker } from './FrameJitterTracker';
import { ReactRenderTracker } from './ReactRenderTracker';
import { SnapshotStatsTracker } from './SnapshotStatsTracker';
import { gameLogger } from '../../utils/GameLogger';
import { isDevMode } from '@/shared/utils/isDevMode';
import { isDebugSurface } from '../../shared/diagnostics/isDebugSurface';

export const PlaytestRigApplier: React.FC = () => {
  const { setEnabled, windows: tools } = useWindows();

  useEffect(() => {
    if (!isDevMode()) return;
    // The rig is a /debug-surface convenience (zero-click diagnostic viewport),
    // NOT a dev-tier default. Without this gate it force-opened its toolsToOpen
    // (smoothnessDashboard/frameJitter/eventRate) on EVERY dev-tier route —
    // including /pve — because isDevMode() is true across the whole PTR tier.
    // DJ 2026-05-29: "if I pick pve it shouldn't load all our stutter detect."
    if (!isDebugSurface()) return;
    const rig = DevConfig.playtestRig;
    if (!rig?.enabled) return;

    const knownIds = new Set(tools.map((t) => t.id));
    const accepted: string[] = [];
    const rejected: string[] = [];
    for (const id of rig.toolsToOpen) {
      if (knownIds.has(id as WindowId)) {
        setEnabled(id as WindowId, true);
        accepted.push(id);
      } else {
        rejected.push(id);
      }
    }

    // Trackers fire on a delay so the cold-start / first-paint / bundle-
    // eval frames don't enter the rolling histogram and pollute the
    // server-side snapshot. Without this delay a fresh PTR session
    // reports "severe" for ~5-15s because the FrameJitterReporter pushes
    // pre-warmup samples to the server.
    const delayMs = rig.trackerEnableDelayMs ?? 10000;
    const timer = setTimeout(() => {
      if (rig.enableFrameJitterTracking) {
        FrameJitterTracker.setEnabled(true);
      }
      if (rig.enableReactRenderTracking) {
        ReactRenderTracker.setEnabled(true);
      }
      if (rig.enableSnapshotStats) {
        SnapshotStatsTracker.setEnabled(true);
      }
      gameLogger.info(
        'playtest-rig',
        `trackers enabled after ${delayMs}ms warmup (jitter=${rig.enableFrameJitterTracking}, reactRender=${rig.enableReactRenderTracking}, snapshotStats=${!!rig.enableSnapshotStats})`,
      );
    }, delayMs);

    gameLogger.info(
      'playtest-rig',
      `applied "${rig.label ?? 'unlabeled'}" — opened ${accepted.length} tools, trackers deferred ${delayMs}ms${rejected.length > 0 ? ` (rejected: ${rejected.join(', ')})` : ''}`,
      { accepted, rejected, rig },
    );

    return () => clearTimeout(timer);
  }, [setEnabled, tools]);

  return null;
};
